var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Editor } from '@monaco-editor/react';
import { ArrowBack } from '@mui/icons-material';
import { Button, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parse } from '../parsing/parse';
import { DEFAULT_FILTER_CONFIGURATION, } from '../parsing/UiTypesSpec';
import { colors } from '../styles/MuiTheme';
import { ErrorBoundary } from './ErrorBoundary';
import { UISelect } from './inputs/UISelect';
import { CustomizeTab } from './tabs/CustomizeTab';
var Pre = function (_a) {
    var line = _a.line;
    if (line.startsWith('{') && line.startsWith('[')) {
        return (_jsx("pre", __assign({ style: {
                backgroundColor: colors.rsBlack,
                color: colors.rsWhite,
                fontSize: '0.8rem',
                padding: '1rem',
                borderRadius: '0.5rem',
            } }, { children: JSON.stringify(JSON.parse(line), null, 2) })));
    }
    else {
        return (_jsx("pre", __assign({ style: {
                backgroundColor: colors.rsBlack,
                color: colors.rsWhite,
                whiteSpace: 'pre-wrap',
            } }, { children: line })));
    }
};
var causeList = function (error) {
    var causes = [];
    var current = error === null || error === void 0 ? void 0 : error.cause;
    while (current) {
        causes.push(current);
        current = current.cause;
    }
    return causes;
};
var VisualResults = function (_a) {
    var parsed = _a.parsed, configOnChange = _a.configOnChange;
    if (!parsed) {
        return _jsx("div", { children: "No parsed result" });
    }
    if (parsed.errors) {
        return (_jsxs("div", { children: [_jsxs(Typography, __assign({ color: "text.primary", variant: "h4" }, { children: [parsed.errors.length, " errors found"] })), parsed.errors.map(function (error, i) { return (_jsxs("div", { children: [_jsx(Typography, __assign({ color: "text.secondary", variant: "body2" }, { children: "An error was encountered handling the structured comment or #define for:" })), _jsx(Pre, { line: error.line }), _jsx(Pre, { line: error.error.toString() }), causeList(error.error).map(function (cause, i) { return (_jsxs("div", { children: [_jsx(Typography, __assign({ color: "text.secondary", variant: "body2" }, { children: "Caused By:" })), _jsx(Pre, { line: cause.toString() })] }, i)); })] }, i)); })] }));
    }
    else {
        return (_jsx(CustomizeTab, { filter: parsed.filter, config: DEFAULT_FILTER_CONFIGURATION, showSettings: false, readonly: false, onChange: configOnChange, clearConfiguration: function () { }, setEnabledModule: function () { } }));
    }
};
export var Rs2fEditor = function (_a) {
    var _b;
    var selected = _a.selected, setSelected = _a.setSelected, filesContent = _a.filesContent, setEditorContent = _a.setEditorContent, options = _a.options, requireMeta = _a.requireMeta, extraTabComponent = _a.extraTabComponent, warningComponent = _a.warningComponent, configOnChange = _a.configOnChange;
    var selectedContent = selected ? filesContent[selected] || '' : '';
    var navigator = useNavigate();
    var metaContentOverride = requireMeta
        ? undefined
        : { name: '__filter_name__' };
    var parsed = typeof selectedContent === 'string'
        ? parse(selectedContent, false, metaContentOverride)
        : null;
    var _c = useState('visual'), tab = _c[0], setTab = _c[1];
    return (_jsxs(ErrorBoundary, { children: [_jsxs("div", __assign({ style: {
                    padding: '1rem',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                } }, { children: [_jsxs(Button, __assign({ color: "primary", variant: "main-action", onClick: function () {
                            navigator('/');
                        } }, { children: [_jsx(ArrowBack, {}), "Back to Customizer"] })), _jsx(UISelect, { disableClearable: true, sx: { width: '15rem' }, options: options ||
                            Object.keys(filesContent).map(function (file) { return ({
                                label: file,
                                value: file,
                            }); }), value: (_b = (options
                            ? options.filter(function (_a) {
                                var value = _a.value;
                                return value === selected;
                            })[0]
                            : undefined)) !== null && _b !== void 0 ? _b : (selected ? { label: selected, value: selected } : null), onChange: function (value) {
                            if (!value) {
                                setSelected(null);
                            }
                            else {
                                setSelected(value.value);
                            }
                        }, label: "Choose a File", multiple: false, freeSolo: false, disabled: false, error: false }), warningComponent] })), _jsxs("div", __assign({ style: {
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '1rem',
                    marginBottom: '1rem',
                } }, { children: [_jsxs(Tabs, __assign({ value: tab, onChange: function (_, newValue) { return setTab(newValue); } }, { children: [_jsx(Tab, { value: "visual", label: "Visual Results" }), _jsx(Tab, { value: "json", label: "Result JSON" }), _jsx(Tab, { value: "wide", label: "Wide Editor" })] })), extraTabComponent] })), _jsxs("div", __assign({ style: { display: 'flex', flexDirection: 'row', gap: '1rem' } }, { children: [_jsx("div", __assign({ style: {
                            width: tab === 'wide' ? '100%' : '50%',
                            minHeight: '75vh',
                        } }, { children: _jsx(Editor, { height: "100%", width: "100%", language: "cpp", theme: "vs-dark", options: {
                                minimap: {
                                    // VERY laggy on large filters
                                    enabled: false,
                                },
                                wordWrap: 'on',
                                readOnly: !selected,
                            }, value: selectedContent, onChange: function (content) {
                                if (selected) {
                                    setEditorContent(selected, content !== null && content !== void 0 ? content : '');
                                }
                            } }) })), _jsx("div", __assign({ style: {
                            width: '50%',
                            display: tab === 'visual' ? 'block' : 'none',
                        } }, { children: _jsx(VisualResults, { parsed: parsed, configOnChange: configOnChange }) })), _jsx("div", __assign({ style: {
                            width: '50%',
                            display: tab === 'json' ? 'block' : 'none',
                        } }, { children: _jsx(Editor, { height: "80vh", width: "100%", language: "json", theme: "vs-dark", value: JSON.stringify(parsed, null, 2), options: {
                                minimap: {
                                    enabled: false,
                                },
                                readOnly: true,
                            } }) }))] }))] }));
};
